import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  AUTO_SUSPEND_QUICK_EXCLUDE_HOSTS,
  DEFAULT_AUTO_SUSPEND_CONFIG,
  type AutoSuspendConfig,
} from '@/types/auto-suspend';

interface AutoSuspendSessionFieldsProps {
  value: AutoSuspendConfig;
  onChange: (next: AutoSuspendConfig) => void;
  disabled?: boolean;
}

function mergeConfig(raw: Partial<AutoSuspendConfig> | undefined): AutoSuspendConfig {
  return {
    ...DEFAULT_AUTO_SUSPEND_CONFIG,
    ...raw,
    excludeDomains: raw?.excludeDomains ?? [],
  };
}

export function AutoSuspendSessionFields({
  value,
  onChange,
  disabled = false,
}: AutoSuspendSessionFieldsProps) {
  const { t } = useTranslation('common');
  const config = mergeConfig(value);
  const [hostDraft, setHostDraft] = useState('');

  const patch = useCallback(
    (partial: Partial<AutoSuspendConfig>) => {
      onChange(mergeConfig({ ...config, ...partial }));
    },
    [config, onChange]
  );

  const addHost = (host: string) => {
    const normalized = host.trim().toLowerCase().replace(/^www\./, '');
    if (!normalized || !normalized.includes('.')) return;
    if (config.excludeDomains?.includes(normalized)) return;
    const next = [...(config.excludeDomains ?? []), normalized].slice(0, 50);
    patch({ excludeDomains: next });
    setHostDraft('');
  };

  const removeHost = (host: string) => {
    patch({
      excludeDomains: (config.excludeDomains ?? []).filter(h => h !== host),
    });
  };

  return (
    <div className="space-y-4 rounded-2xl border border-border-muted bg-muted/20 px-4 py-4">
      <p className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">
        {t('sessions.memory.autoSuspendTitle')}
      </p>
      <p className="text-xs leading-relaxed text-muted-foreground px-1">
        {t('sessions.memory.autoSuspendDescription')}
      </p>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            {t('sessions.memory.autoSuspendEnableLabel')}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          disabled={disabled}
          aria-checked={config.enabled}
          aria-label={t('sessions.memory.autoSuspendEnableLabel')}
          onClick={() => patch({ enabled: !config.enabled })}
          className={
            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ' +
            (config.enabled ? 'bg-primary' : 'bg-muted')
          }
        >
          <span
            aria-hidden
            className={
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ' +
              (config.enabled ? 'translate-x-5' : 'translate-x-0')
            }
          />
        </button>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground" htmlFor="auto-suspend-idle-minutes">
          {t('sessions.memory.autoSuspendIdleLabel')}
        </label>
        <input
          id="auto-suspend-idle-minutes"
          type="number"
          min={5}
          max={240}
          disabled={disabled}
          value={config.idleMinutes}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (Number.isFinite(n)) patch({ idleMinutes: n });
          }}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
        />
        <p className="text-xs text-muted-foreground">
          {t('sessions.memory.autoSuspendIdleHelper')}
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          {t('sessions.memory.autoSuspendExcludeLabel')}
        </p>
        <div className="flex flex-wrap gap-2">
          {AUTO_SUSPEND_QUICK_EXCLUDE_HOSTS.map(host => (
            <button
              key={host}
              type="button"
              disabled={disabled}
              onClick={() => addHost(host)}
              className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
            >
              + {host}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            disabled={disabled}
            value={hostDraft}
            onChange={(e) => setHostDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addHost(hostDraft);
              }
            }}
            placeholder={t('sessions.memory.autoSuspendExcludePlaceholder')}
            className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            type="button"
            disabled={disabled || !hostDraft.trim()}
            onClick={() => addHost(hostDraft)}
            className="shrink-0 rounded-xl border border-border px-3 py-2 text-sm font-medium hover:bg-muted/50"
          >
            {t('sessions.memory.autoSuspendExcludeAdd')}
          </button>
        </div>
        {(config.excludeDomains ?? []).length > 0 ? (
          <ul className="flex flex-wrap gap-2 pt-1">
            {(config.excludeDomains ?? []).map(host => (
              <li
                key={host}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-foreground"
              >
                {host}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={t('sessions.memory.autoSuspendExcludeRemove', { host })}
                  onClick={() => removeHost(host)}
                  className="rounded p-0.5 hover:bg-background"
                >
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
